import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FruitifyContainer } from "../../../shared-components/Containers";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Pagination,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import Grid from "@mui/material/Grid";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import { formatDistanceToNow } from "date-fns";

const ReviewListItem = ({ review }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Chip label={review.contentType} color="primary" size="small" />

        <Typography variant="body2" color="text.secondary">
          {review.message}
        </Typography>
      </Stack>
      {review.objectId && (
        <Typography variant="body2" color="text.secondary">
          <strong>Object: </strong>
          {review.objectId}
        </Typography>
      )}
    </Box>
  );
};

const WondeReviewList = ({ reviews }) => {
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(reviews.length / PAGE_SIZE);

  const displayedReviews = reviews.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const handlePageChange = (_event, value) => {
    setPage(value);
  };

  return (
    <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
      <Typography variant="h5">Reviews</Typography>
      {displayedReviews.length === 0 && (
        <Typography variant={"subtitle1"}>No reviews to show</Typography>
      )}
      {displayedReviews.map((review, index) => (
        <ReviewListItem key={index} review={review} />
      ))}

      {totalPages > 1 && (
        <Stack alignItems="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Stack>
      )}
    </Stack>
  );
};

const WondeSyncSummary = ({ wondeSync }) => {
  return (
    <Card data-testid={`wonde-sync-summary-${wondeSync?.id}`}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h3">Wonde sync</Typography>
        </Stack>
      </CardContent>
      <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
        <Grid size={4}>
          <InformationSnippet title={"School"} value={wondeSync.school?.name} />
        </Grid>
        <Grid size={4}>
          <InformationSnippet title={"Status"} value={wondeSync.syncStatus} />
        </Grid>
        <Grid size={4}>
          <InformationSnippet
            title={"Last update"}
            value={formatDistanceToNow(new Date(wondeSync.lastUpdated), {
              addSuffix: true,
            })}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

function WondeSyncView() {
  const { syncId } = useParams();
  const ApiClient = useApiClient();
  const [wondeSync, setWondeSync] = useState({});
  const [loading, setLoading] = useState(false);

  const getWondeSync = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.wonde.getWondeSync({
        wondeSync: syncId,
      });
      if (response) {
        setWondeSync(response.wondeSync);
      }
    } catch (error) {
      console.error("Failed to fetch assignment", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getWondeSync();
  }, [syncId]);

  if (loading) {
    return <GeneralLoading />;
  }

  if (!Object.keys(wondeSync).length) {
    return <Typography>Unable to load wonde sync data.</Typography>;
  }

  return (
    <FruitifyContainer>
      <WondeSyncSummary wondeSync={wondeSync} />
      <WondeReviewList reviews={wondeSync.reviews} />
    </FruitifyContainer>
  );
}

export default WondeSyncView;
